import { FC, ComponentProps, useMemo, useState, useRef, useEffect } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import {
  Colors,
  DropDown,
  DropDownItem,
  ItemInfo,
  RenderCallback,
  prepareItemsForDropDown,
  useStyle,
  useThemeValue,
} from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { SelectItem } from '../types';
import { WrappedInput } from '../WrappedInput';

type Props = Omit<ComponentProps<typeof DropDown>, 'items' | 'setOpen' | 'onSelect'> & {
  control: Control<FieldValues>;
  name: string;
  additonalProps?: Record<string, unknown>;
  items: SelectItem[];
  defaultValue?: ComponentProps<typeof Controller>['defaultValue'];
  searchActive?: boolean;
  loading?: boolean;
  maximumLabelAsPlaceholderToShown?: number;
  hasBorderColor?: boolean;
};

export const WrappedMultipleDropdown: FC<Props> = ({
  control,
  name,
  additonalProps = {},
  searchActive = false,
  defaultValue,
  loading,
  maximumLabelAsPlaceholderToShown = 2,
  hasBorderColor = false,
  ...props
}) => {
  const t = useTranslations('inputs');
  const [open, setOpen] = useState<boolean>(false);
  const themeMode = useSelector(getModeTheme());

  const selectFilter = (item: DropDownItem<SelectItem>, searchValue: string) =>
    (item as unknown as DropDownItem<SelectItem>).item.label
      .toUpperCase()
      .includes(searchValue.toUpperCase());

  const items = useMemo(
    () =>
      prepareItemsForDropDown([...props.items], {
        defaultSelected: [],
      }),
    [props.items],
  );

  const contentExtractor: RenderCallback<SelectItem, ItemInfo> = ({ item }) => {
    return {
      text: item.label,
      textColor: 'primary/text',
      checkBox: true,
      disabled: false,
    };
  };

  const searchProp = useMemo(() => {
    const MIN_ITEMS = 1;

    if (items.length < MIN_ITEMS) {
      return undefined;
    }

    return {
      placeholder: t('placeholderSearch'),
      filterCallback: selectFilter,
    };
  }, [items.length, t]);

  const color: keyof Colors = 'primary/text';
  const parsedColor = useStyle({ color });

  const backgroundColorHeader = useThemeValue('colors', 'secondary/background');

  const ref = useRef(null);

  const clickChecker = (event: MouseEvent) => {
    const path = event.composedPath();
    const dropdownElement = ref.current;

    if (dropdownElement && !path.includes(dropdownElement)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => clickChecker(e);
    if (ref.current) {
      document.addEventListener('click', handleClick);
    }
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div ref={ref}>
      <Controller
        control={control}
        name={name}
        rules={{ ...additonalProps }}
        defaultValue={defaultValue}
        render={({ field }) => {
          const selectedItems = Array.isArray(field.value) ? field.value : [];
          const elements = items.map(item => {
            return {
              ...item,
              selected: selectedItems.find(
                (elm: { value: string }) => elm.value === item.item.value,
              ),
            };
          });

          if (loading) {
            return (
              <WrappedInput
                name={name}
                control={control}
                value={
                  selectedItems
                    ? selectedItems.flatMap((i: { label: string }) => i.label).join(', ')
                    : props.placeholder
                }
                suffixSvg='loading'
                suffix='loading'
                disabled
                loading
                iconStyle={themeMode === 'dark' ? { color: 'white' } : undefined}
                label={props.label || ''}
              />
            );
          }

          return (
            <DropDown
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              labelStyle={{ mb: 'xs', color }}
              items={elements}
              label={props.label || ''}
              placeholder={
                selectedItems
                  ? selectedItems
                      .flatMap((i: { label: string }) => i.label)
                      .slice(0, maximumLabelAsPlaceholderToShown)
                      .join(', ') +
                    (selectedItems.length > maximumLabelAsPlaceholderToShown ? ', ...' : '')
                  : props.placeholder
              }
              searchable={searchActive ? searchProp : undefined}
              showSvg
              dropDownHeaderStyle={
                {
                  border: hasBorderColor ? `1px solid ${parsedColor.color} !important` : undefined,
                  textStyle: {
                    mr: 'xs',
                    overflow: 'hidden',
                    numberOfLines: 1,
                    ellipsis: true,
                  },
                  height: 44,
                  background: backgroundColorHeader,
                } as never
              }
              overlay
              zIndex={1000}
              maxHeight={props.maxHeight || 300}
              keyExtractor={(item: SelectItem) => item.value}
              contentExtractor={contentExtractor}
              open={open}
              setOpen={() => setOpen(!open)}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onSelect={(item: any) => {
                const selected = Array.isArray(field.value) ? field.value : [];
                if (!selected?.find((elm: { value: string }) => elm.value === item.item.value)) {
                  field.onChange([...(selected || []), item.id]);
                  return;
                }
                const removedSelected = selected?.filter(
                  (selectedItem: { value: string }) => selectedItem.value !== item.item.value,
                );
                field.onChange([...removedSelected]);
              }}
            />
          );
        }}
      />
    </div>
  );
};
